import Analytics from '@/utils/analytics';
import ChevronLeftIcon from '@assets/icons/chevron-left.svg';
import ChevronRightIcon from '@assets/icons/chevron-right.svg';
import React, { type FC, useCallback, useState } from 'react';
import { TabBar as BaseTabBar, TabBarItem } from 'react-native-tab-view';
import { createStyles } from '@/contexts/theme-context';
import { FadingComponent } from '@/components/fading-component';
import { type OrderInboundsDetailTabsRoute } from './types';
import { Platform, Pressable, View } from 'react-native';
import { TabView, type Props as TabViewProps } from '@/components/tab-view';

const ESTIMATED_TAB_WIDTH = 140;

export type OrderInboundsDetailTabViewProps =
  TabViewProps<OrderInboundsDetailTabsRoute>;

export const OrderInboundsDetailTabView: FC<
  OrderInboundsDetailTabViewProps
> = ({ indicatorStyle, label, tabBarTabStyle, style, ...rest }) => {
  const [styles, theme] = useStyles();
  const [layoutWidth, setLayoutWidth] = useState(0);

  const currentIndex = rest.navigationState.index;
  const totalRoutes = rest.navigationState.routes.length;
  const canGoLeft = currentIndex > 0;
  const canGoRight = currentIndex < totalRoutes - 1;

  const estimatedContentWidth = totalRoutes * ESTIMATED_TAB_WIDTH;

  const showArrows =
    Platform.OS === 'web' &&
    layoutWidth > 0 &&
    estimatedContentWidth > layoutWidth;

  const renderTabBar = useCallback(
    (tabBarProps: Parameters<NonNullable<typeof rest.renderTabBar>>[0]) => {
      return (
        <View
          onLayout={(e) => {
            setLayoutWidth(e.nativeEvent.layout.width);
          }}
          style={styles.tabBarContainer}
        >
          <BaseTabBar
            {...tabBarProps}
            activeColor={theme.colors.primary}
            inactiveColor={theme.colors.inactiveText}
            indicatorStyle={[styles.tabBarIndicator, indicatorStyle]}
            pressOpacity={0.8}
            renderTabBarItem={(tabBarItemProps) => (
              <TabBarItem
                {...tabBarItemProps}
                key={tabBarItemProps.key}
                label={label}
              />
            )}
            scrollEnabled={rest.scrollEnabled}
            style={[styles.tabBar, style]}
            tabStyle={[styles.tabBarTab, tabBarTabStyle]}
          />
          {showArrows ? (
            <>
              <FadingComponent
                isVisible={canGoLeft}
                style={[styles.arrowButton, styles.arrowButtonLeft]}
              >
                <Pressable
                  onPress={() => {
                    Analytics.track(
                      'order-inbounds-detail-tabs.left_button.press',
                    );
                    if (canGoLeft && rest.onIndexChange) {
                      rest.onIndexChange(currentIndex - 1);
                    }
                  }}
                  style={({ pressed }) => [
                    styles.arrowTouchable,
                    { opacity: pressed ? 0.5 : 1 },
                  ]}
                >
                  <ChevronLeftIcon fill={theme.colors.white} />
                </Pressable>
              </FadingComponent>
              <FadingComponent
                isVisible={canGoRight}
                style={[styles.arrowButton, styles.arrowButtonRight]}
              >
                <Pressable
                  onPress={() => {
                    Analytics.track(
                      'order-inbounds-detail-tabs.right_button.press',
                    );
                    if (canGoRight && rest.onIndexChange) {
                      rest.onIndexChange(currentIndex + 1);
                    }
                  }}
                  style={({ pressed }) => [
                    styles.arrowTouchable,
                    { opacity: pressed ? 0.5 : 1 },
                  ]}
                >
                  <ChevronRightIcon fill={theme.colors.white} />
                </Pressable>
              </FadingComponent>
            </>
          ) : null}
        </View>
      );
    },
    [
      showArrows,
      canGoLeft,
      canGoRight,
      currentIndex,
      styles,
      theme.colors.white,
      theme.colors.primary,
      theme.colors.inactiveText,
      indicatorStyle,
      label,
      tabBarTabStyle,
      style,
      rest,
    ],
  );

  return (
    <TabView
      {...rest}
      indicatorStyle={indicatorStyle}
      label={label}
      renderTabBar={renderTabBar}
      scrollEnabled={rest.scrollEnabled}
      style={style}
      tabBarTabStyle={tabBarTabStyle}
    />
  );
};

const useStyles = createStyles((theme) => ({
  arrowButton: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    width: 32,
    zIndex: 10,
  },
  arrowButtonLeft: {
    left: 0,
  },
  arrowButtonRight: {
    right: 0,
  },
  arrowTouchable: {
    alignItems: 'center',
    backgroundColor: theme.colors.primaryOverlay,
    bottom: 0,
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    width: 32,
  },
  tabBar: {
    backgroundColor: theme.colors.white,
    borderBottomColor: 'transparent',
    borderBottomWidth: 0,
    elevation: 0,
    shadowOpacity: 0,
  },
  tabBarContainer: {
    position: 'relative',
  },
  tabBarIndicator: {
    backgroundColor: theme.colors.accent,
    height: 4,
  },
  tabBarTab: {
    minHeight: 0,
    paddingVertical: 16,
  },
}));
